import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Code,
  Globe
} from 'lucide-react';

export default function APISettings({ selectedTeam }) {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (selectedTeam) {
      loadApiKeys();
      checkOwnership();
    }
  }, [selectedTeam]);

  const checkOwnership = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/role`);
      setIsOwner(res.data.role === 'owner');
    } catch (err) {
      setIsOwner(false);
    }
  };

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/api-keys`);
      setApiKeys(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setIsOwner(false);
      } else {
        toast.error('API anahtarları yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      toast.error('Anahtar adı gerekli');
      return;
    }

    setCreating(true);
    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/api-keys`, {
        name: newKeyName.trim()
      });
      setNewlyCreatedKey(res.data);
      setNewKeyName('');
      loadApiKeys();
      toast.success('API anahtarı oluşturuldu');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Oluşturma başarısız');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (keyId) => {
    if (!confirm('Bu API anahtarını silmek istediğinize emin misiniz?')) return;

    try {
      await axiosInstance.delete(`/teams/${selectedTeam}/api-keys/${keyId}`);
      setApiKeys(apiKeys.filter(k => k.id !== keyId));
      toast.success('API anahtarı silindi');
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopyalandı');
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="w-96 text-center">
          <CardContent className="pt-8 pb-8">
            <Key className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">Lütfen bir mağaza seçin</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isOwner && !loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="w-96 text-center">
          <CardContent className="pt-8 pb-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-lg font-semibold mb-2">Erişim Kısıtlı</h3>
            <p className="text-gray-500">Sadece mağaza sahipleri API anahtarlarını yönetebilir.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          API Ayarları
        </h1>
        <p className="text-gray-600 mt-1">
          Marka ve kategori verilerinizi dışarıya aktarmak için API anahtarları oluşturun
        </p>
      </div>

      {/* API Documentation */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Code className="w-5 h-5" />
            API Kullanımı
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-800 mb-3">
            API anahtarınızı <code className="bg-blue-100 px-1 rounded">X-API-Key</code> header'ı ile gönderin:
          </p>
          <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono overflow-x-auto">
            <p className="text-gray-500"># Tüm markaları getir</p>
            <p>curl -H "X-API-Key: YOUR_API_KEY" \</p>
            <p className="ml-4">{backendUrl}/api/public/brands</p>
            <p className="mt-2 text-gray-500"># Tüm kategorileri getir</p>
            <p>curl -H "X-API-Key: YOUR_API_KEY" \</p>
            <p className="ml-4">{backendUrl}/api/public/categories</p>
            <p className="mt-2 text-gray-500"># Komple menü (marka + kategori + butonlar)</p>
            <p>curl -H "X-API-Key: YOUR_API_KEY" \</p>
            <p className="ml-4">{backendUrl}/api/public/menu</p>
            <p className="mt-2 text-gray-500"># Bağlı kanallar (ana + info) — bot ile detay çözümü</p>
            <p>curl -H "X-API-Key: YOUR_API_KEY" \</p>
            <p className="ml-4">{backendUrl}/api/public/channels</p>
            <p className="mt-2 text-gray-500"># Mağaza özet bilgisi</p>
            <p>curl -H "X-API-Key: YOUR_API_KEY" \</p>
            <p className="ml-4">{backendUrl}/api/public/info</p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a 
              href={`${backendUrl}/docs#/Public%20API`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              API Dokümantasyonu
            </a>
          </div>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-purple-500" />
                API Anahtarları
              </CardTitle>
              <CardDescription>
                {apiKeys.length} anahtar oluşturulmuş
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-gradient-to-r from-purple-500 to-pink-500">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Anahtar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">Henüz API anahtarı oluşturulmamış</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                İlk Anahtarı Oluştur
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div 
                  key={key.id}
                  className="flex items-center justify-between p-4 rounded-xl border bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Key className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">{key.name}</p>
                      <p className="text-sm text-gray-500 font-mono">{key.key_preview}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {key.last_used && (
                      <div className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Son: {new Date(key.last_used).toLocaleDateString('tr-TR')}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(key.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni API Anahtarı</DialogTitle>
            <DialogDescription>
              Bu anahtar oluşturulduktan sonra sadece bir kez gösterilecek
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Anahtar Adı</Label>
              <Input
                placeholder="Örn: Web Sitem, Mobil Uygulama"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Bu isim anahtarı tanımlamanıza yardımcı olacak
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Oluşturuluyor...' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newly Created Key Dialog */}
      <Dialog open={!!newlyCreatedKey} onOpenChange={() => setNewlyCreatedKey(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              API Anahtarı Oluşturuldu
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Önemli!</p>
                  <p className="text-sm text-yellow-700">
                    Bu API anahtarı sadece bir kez gösterilecek. Güvenli bir yere kaydedin!
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <Label>API Anahtarı</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={newlyCreatedKey?.api_key || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(newlyCreatedKey?.api_key)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewlyCreatedKey(null)}>
              Tamam, Kaydettim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
